<template>
  <Block v-if="moduleCards.length" :title="t('title')" emoji="🎓">
    <template v-if="canFollowStoresCompletion" #actions>
      <router-link
        :to="{ path: '/admin/training', query: { tab: 'stores' } }"
        class="inline-flex items-center gap-1 rounded-lg bg-primary-light px-3 py-1.5 text-xs font-semibold text-primary-dark whitespace-nowrap hover:bg-primary-lighter transition-colors"
      >
        {{ t("stores_follow_up_link") }}
        <ArrowRightIcon class="h-3 w-3" />
      </router-link>
    </template>
    <div class="space-y-4 px-6 pb-4">
      <div v-for="card in moduleCards" :key="card.id" class="rounded-lg border border-gray-100 p-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0 flex-1">
            <h4 class="text-sm font-semibold text-gray-900 sm:truncate">{{ card.title }}</h4>
            <template v-if="card.hasStats">
              <p class="mt-1 text-xs text-gray-600">{{ card.summary }}</p>
              <ProgressionBar :max="card.progress.max" :value="card.progress.value" class="mt-1" />

              <SellerAvatarGroup :sellers="card.passedSellers" size="sm" class="mt-2">
                <template #follow-up>
                  <span v-if="card.followUp" class="text-orange-600">{{ card.followUp }}</span>
                </template>
              </SellerAvatarGroup>
            </template>
          </div>

          <router-link
            :to="`/admin/training/module/${card.id}`"
            class="inline-flex items-center justify-center gap-1 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white whitespace-nowrap hover:bg-gray-800 transition-colors"
          >
            {{ t("start_module") }}
            <ArrowRightIcon class="h-3 w-3" />
          </router-link>
        </div>
      </div>
    </div>
  </Block>
</template>

<script>
import { Block, ProgressionBar } from "@estaly/ui";
import { ArrowRightIcon } from "@heroicons/vue/outline";
import SellerAvatarGroup from "@/components/training-module/SellerAvatarGroup.vue";
import { mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";

function percentOf(count, total) {
  return total === 0 ? 0 : Math.round((count / total) * 100);
}

export default {
  name: "TrainingBlock",
  components: {
    Block,
    ProgressionBar,
    ArrowRightIcon,
    SellerAvatarGroup,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  computed: {
    ...mapGetters("auth", ["accessibleStoreIds"]),
    ...mapGetters("trainingModules", ["canFollowStoresCompletion"]),
    ...mapState("trainingModules", ["trainingModules"]),
    isMultiStore() {
      return this.accessibleStoreIds.length >= 2;
    },
    moduleCards() {
      return (this.trainingModules || []).map((mod) => this.buildModuleCard(mod));
    },
  },
  created() {
    if (this.accessibleStoreIds.length) {
      this.$store.dispatch("trainingModules/fetchIndex", this.accessibleStoreIds);
    }
  },
  methods: {
    buildModuleCard(mod) {
      const stats = mod.trainingStats || {};
      const totalSellers = stats.totalSellers ?? 0;
      const passedCount = stats.passedCount ?? 0;
      const sellersSummary = this.t("sellers_trained", {
        passed: passedCount,
        total: totalSellers,
        percent: percentOf(passedCount, totalSellers),
      });
      const card = {
        id: mod.id,
        title: mod.title,
        hasStats: totalSellers > 0,
        passedSellers: stats.passedSellers ?? [],
      };

      // Admins with several stores follow the progress by store rather than by seller
      if (this.isMultiStore && stats.totalStores > 0) {
        const { totalStores, trainedStoresCount } = stats;
        const storesToFollowUp = totalStores - trainedStoresCount;
        const storesSummary = this.t("stores_trained", {
          trained: trainedStoresCount,
          total: totalStores,
          percent: percentOf(trainedStoresCount, totalStores),
        });
        return {
          ...card,
          summary: `${storesSummary} · ${sellersSummary}`,
          progress: { value: trainedStoresCount, max: totalStores },
          followUp: storesToFollowUp > 0 ? this.t("stores_to_follow_up", storesToFollowUp) : null,
        };
      }

      const toFollowUpCount = stats.toFollowUpCount ?? 0;
      return {
        ...card,
        summary: sellersSummary,
        progress: { value: passedCount, max: totalSellers },
        followUp: toFollowUpCount > 0 ? this.t("to_follow_up", { count: toFollowUpCount }) : null,
      };
    },
  },
};
</script>

<i18n src="./TrainingBlock.i18n.json"></i18n>
